import React, { useState, useEffect, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Chip,
  Box,
  Paper,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import moment from "moment";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  templatePaper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  }
}));

const CampaignMetaSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, "Nome muito curto")
    .max(100, "Nome muito longo")
    .required("Campo obrigatório"),
  integrationId: Yup.number().required("Selecione uma conexão WhatsApp Oficial"),
  template1Name: Yup.string().required("Selecione pelo menos um template"),
  scheduledAt: Yup.string().required("Data de agendamento obrigatória"),
  distributionStrategy: Yup.string().required("Selecione uma estratégia"),
  statusTicket: Yup.string().required("Campo obrigatório"),
  openTicket: Yup.string().required("Campo obrigatório"),
  // Campos opcionais com transform
  contactListId: Yup.number()
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .nullable(),
  tagListId: Yup.number()
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .nullable(),
  userId: Yup.number()
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .nullable(),
  queueId: Yup.number()
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .nullable(),
  // Validação de recorrência
  isRecurring: Yup.boolean().default(false),
  recurrenceType: Yup.string().when('isRecurring', {
    is: true,
    then: Yup.string().oneOf(['minutely', 'hourly', 'daily', 'weekly', 'biweekly', 'monthly', 'yearly']).required('Tipo de recorrência é obrigatório'),
    otherwise: Yup.string().nullable()
  }),
  recurrenceInterval: Yup.number().when('isRecurring', {
    is: true,
    then: Yup.number().min(1, 'Intervalo deve ser maior que 0').required('Intervalo é obrigatório'),
    otherwise: Yup.number().nullable()
  }),
  recurrenceDaysOfWeek: Yup.array().when(['isRecurring', 'recurrenceType'], {
    is: (isRecurring, recurrenceType) => isRecurring && recurrenceType === 'weekly',
    then: Yup.array().min(1, 'Selecione pelo menos um dia da semana').required(),
    otherwise: Yup.array().nullable()
  }),
  recurrenceDayOfMonth: Yup.number().when(['isRecurring', 'recurrenceType'], {
    is: (isRecurring, recurrenceType) => isRecurring && recurrenceType === 'monthly',
    then: Yup.number().min(1, 'Dia deve ser entre 1 e 31').max(31, 'Dia deve ser entre 1 e 31').required('Dia do mês é obrigatório'),
    otherwise: Yup.number().nullable()
  }),
  recurrenceEndDate: Yup.date()
    .transform((value, originalValue) => {
      // Transformar string vazia em null
      return originalValue === '' ? null : value;
    })
    .nullable()
    .when('isRecurring', {
      is: true,
      then: Yup.date().min(new Date(), 'Data final deve ser futura').nullable(),
      otherwise: Yup.date().nullable()
    }),
  maxExecutions: Yup.number().when('isRecurring', {
    is: true,
    then: Yup.number().min(1, 'Número máximo deve ser maior que 0').nullable(),
    otherwise: Yup.number().nullable()
  })
});

const CampaignMetaModal = ({ open, onClose, campaignMetaId }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { companyId } = user;
  const [loading, setLoading] = useState(false);
  const [whatsappConnections, setWhatsappConnections] = useState([]);
  const [contactLists, setContactLists] = useState([]);
  const [tags, setTags] = useState([]);
  const [users, setUsers] = useState([]);
  const [queues, setQueues] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState({
    template1: null,
    template2: null,
    template3: null
  });

  const initialState = {
    name: "",
    integrationId: "",
    template1Name: "",
    template1Config: { language: { code: "pt_BR" }, components: [] },
    template2Name: "",
    template2Config: null,
    template3Name: "",
    template3Config: null,
    distributionStrategy: "random",
    scheduledAt: moment().add(1, "hour").format("YYYY-MM-DDTHH:mm"),
    contactListId: "",
    tagListId: "",
    userId: "",
    queueId: "",
    statusTicket: "closed",
    openTicket: "disabled",
    isRecurring: false,
    recurrenceType: "",
    recurrenceInterval: 1,
    recurrenceDaysOfWeek: [],
    recurrenceDayOfMonth: 1,
    recurrenceEndDate: "",
    maxExecutions: null
  };

  const [campaignMeta, setCampaignMeta] = useState(initialState);

  useEffect(() => {
    if (!open) return;
    
    const fetchData = async () => {
      try {
        
        const { data: whatsapps } = await api.get("/whatsapp", {
          params: { session: 0 }
        });
        
        // Filtrar conexões WhatsApp Oficial (identificadas por phone_number_id, waba_id, business_id ou integrationId)
        const oficialWhatsapps = Array.isArray(whatsapps) 
          ? whatsapps.filter(w => w.phone_number_id || w.waba_id || w.business_id || w.integrationId)
          : [];
        setWhatsappConnections(oficialWhatsapps);

        const { data: lists } = await api.get("/contact-lists/list", { params: { companyId } });
        setContactLists(Array.isArray(lists) ? lists : []);

        const { data: tagsData } = await api.get("/tags/list");
        setTags(Array.isArray(tagsData) ? tagsData : []);

        const { data: usersData } = await api.get("/users");
        setUsers(Array.isArray(usersData?.users) ? usersData.users : []);

        const { data: queuesData } = await api.get("/queue");
        setQueues(Array.isArray(queuesData) ? queuesData : []);
        
      } catch (err) {
        console.error('[CampaignMetaModal] Erro ao carregar dados:', err);
        toastError(err);
      }
    };

    fetchData();
  }, [open]);

  useEffect(() => {
    const fetchCampaignMeta = async () => {
      if (!campaignMetaId) return;

      try {
        setLoading(true);
        const { data } = await api.get(`/campaigns-meta/${campaignMetaId}`);
        
        setCampaignMeta({
          ...data,
          scheduledAt: moment(data.scheduledAt).format("YYYY-MM-DDTHH:mm"),
          recurrenceEndDate: data.recurrenceEndDate 
            ? moment(data.recurrenceEndDate).format("YYYY-MM-DD")
            : "",
          recurrenceDaysOfWeek: data.recurrenceDaysOfWeek 
            ? JSON.parse(data.recurrenceDaysOfWeek)
            : []
        });

        // Carregar templates da conexão
        if (data.integrationId) {
          await loadTemplates(data.integrationId);
        }

        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    };

    fetchCampaignMeta();
  }, [campaignMetaId, open]);

  const loadTemplates = async (integrationId) => {
    try {
      setLoading(true);
      const { data } = await api.get(`/campaigns-meta/templates/${integrationId}`);
      // O backend retorna { data: [...] }, então acessamos data.data
      const templatesList = Array.isArray(data?.data) ? data.data : [];
      setTemplates(templatesList);
      setLoading(false);
    } catch (err) {
      console.error('[CampaignMetaModal] Erro ao carregar templates:', err);
      toastError(err);
      setTemplates([]);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCampaignMeta(initialState);
    setSelectedTemplates({ template1: null, template2: null, template3: null });
    setTemplates([]);
    onClose();
  };

  const handleSaveCampaignMeta = async (values) => {
    try {
      setLoading(true);

      const campaignData = {
        ...values,
        template1Config: selectedTemplates.template1 
          ? { language: { code: "pt_BR" }, components: [] }
          : values.template1Config,
        template2Config: selectedTemplates.template2 && values.template2Name
          ? { language: { code: "pt_BR" }, components: [] }
          : null,
        template3Config: selectedTemplates.template3 && values.template3Name
          ? { language: { code: "pt_BR" }, components: [] }
          : null,
      };

      if (campaignMetaId) {
        await api.put(`/campaigns-meta/${campaignMetaId}`, campaignData);
        toast.success("Campanha Meta atualizada com sucesso!");
      } else {
        await api.post("/campaigns-meta", campaignData);
        toast.success("Campanha Meta criada com sucesso!");
      }

      setLoading(false);
      handleClose();
    } catch (err) {
      toastError(err);
      setLoading(false);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {campaignMetaId
            ? i18n.t("campaignMetaModal.title.edit")
            : i18n.t("campaignMetaModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={campaignMeta}
          enableReinitialize={true}
          validationSchema={CampaignMetaSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveCampaignMeta(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      label="Nome da Campanha"
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      error={touched.integrationId && Boolean(errors.integrationId)}
                    >
                      <InputLabel>Conexão WhatsApp Oficial</InputLabel>
                      <Field
                        as={Select}
                        label="Conexão WhatsApp Oficial"
                        name="integrationId"
                        onChange={(e) => {
                          const whatsappId = e.target.value;
                          setFieldValue("integrationId", whatsappId);
                          // Carregar templates usando o ID do whatsapp
                          loadTemplates(whatsappId);
                          // Limpar templates selecionados
                          setFieldValue("template1Name", "");
                          setFieldValue("template2Name", "");
                          setFieldValue("template3Name", "");
                          setSelectedTemplates({ template1: null, template2: null, template3: null });
                        }}
                      >
                        <MenuItem value="">Selecione...</MenuItem>
                        {(whatsappConnections || []).map((whatsapp) => (
                          <MenuItem key={whatsapp.id} value={whatsapp.id}>
                            {whatsapp.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>Estratégia de Distribuição</InputLabel>
                      <Field
                        as={Select}
                        label="Estratégia de Distribuição"
                        name="distributionStrategy"
                      >
                        <MenuItem value="random">Aleatório</MenuItem>
                        <MenuItem value="sequential">Sequencial</MenuItem>
                        <MenuItem value="round-robin">Round Robin</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* Templates */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Templates (1-3)
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper className={classes.templatePaper}>
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel>Template 1 (Obrigatório)</InputLabel>
                        <Field
                          as={Select}
                          label="Template 1 (Obrigatório)"
                          name="template1Name"
                          onChange={(e) => {
                            const template = templates.find(t => t.name === e.target.value);
                            setFieldValue("template1Name", e.target.value);
                            setSelectedTemplates(prev => ({ ...prev, template1: template }));
                          }}
                          disabled={!values.integrationId || loading}
                        >
                          <MenuItem value="">Selecione...</MenuItem>
                          {(templates || []).filter(t => t.status === "APPROVED").map((template) => (
                            <MenuItem key={template.id} value={template.name}>
                              {template.name} ({template.language})
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                      {selectedTemplates.template1 && (
                        <Box mt={1}>
                          <Typography variant="caption" color="textSecondary">
                            Categoria: {selectedTemplates.template1.category}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper className={classes.templatePaper}>
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel>Template 2 (Opcional)</InputLabel>
                        <Field
                          as={Select}
                          label="Template 2 (Opcional)"
                          name="template2Name"
                          onChange={(e) => {
                            const template = templates.find(t => t.name === e.target.value);
                            setFieldValue("template2Name", e.target.value);
                            setSelectedTemplates(prev => ({ ...prev, template2: template }));
                          }}
                          disabled={!values.integrationId || !values.template1Name || loading}
                        >
                          <MenuItem value="">Nenhum</MenuItem>
                          {(templates || []).filter(t => t.status === "APPROVED" && t.name !== values.template1Name).map((template) => (
                            <MenuItem key={template.id} value={template.name}>
                              {template.name} ({template.language})
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </Paper>
                  </Grid>

                  <Grid item xs={12}>
                    <Paper className={classes.templatePaper}>
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel>Template 3 (Opcional)</InputLabel>
                        <Field
                          as={Select}
                          label="Template 3 (Opcional)"
                          name="template3Name"
                          onChange={(e) => {
                            const template = templates.find(t => t.name === e.target.value);
                            setFieldValue("template3Name", e.target.value);
                            setSelectedTemplates(prev => ({ ...prev, template3: template }));
                          }}
                          disabled={!values.integrationId || !values.template1Name || loading}
                        >
                          <MenuItem value="">Nenhum</MenuItem>
                          {(templates || []).filter(t => 
                            t.status === "APPROVED" && 
                            t.name !== values.template1Name && 
                            t.name !== values.template2Name
                          ).map((template) => (
                            <MenuItem key={template.id} value={template.name}>
                              {template.name} ({template.language})
                            </MenuItem>
                          ))}
                        </Field>
                      </FormControl>
                    </Paper>
                  </Grid>

                  {/* Destinatários */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Destinatários
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>Lista de Contatos</InputLabel>
                      <Field
                        as={Select}
                        label="Lista de Contatos"
                        name="contactListId"
                      >
                        <MenuItem value="">Nenhuma</MenuItem>
                        {(contactLists || []).map((list) => (
                          <MenuItem key={list.id} value={list.id}>
                            {list.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>Tag</InputLabel>
                      <Field
                        as={Select}
                        label="Tag"
                        name="tagListId"
                      >
                        <MenuItem value="">Nenhuma</MenuItem>
                        {(tags || []).map((tag) => (
                          <MenuItem key={tag.id} value={tag.id}>
                            {tag.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* Agendamento */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Agendamento
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Field
                      as={TextField}
                      label="Data e Hora"
                      name="scheduledAt"
                      type="datetime-local"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.isRecurring}
                          onChange={(e) => setFieldValue("isRecurring", e.target.checked)}
                          name="isRecurring"
                          color="primary"
                        />
                      }
                      label="Campanha Recorrente"
                    />
                  </Grid>

                  {values.isRecurring && (
                    <>
                      <Grid item xs={12} sm={6}>
                        <FormControl variant="outlined" margin="dense" fullWidth>
                          <InputLabel>Tipo de Recorrência</InputLabel>
                          <Field
                            as={Select}
                            label="Tipo de Recorrência"
                            name="recurrenceType"
                          >
                            <MenuItem value="daily">Diário</MenuItem>
                            <MenuItem value="weekly">Semanal</MenuItem>
                            <MenuItem value="monthly">Mensal</MenuItem>
                          </Field>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label="Intervalo"
                          name="recurrenceInterval"
                          type="number"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label="Data Final"
                          name="recurrenceEndDate"
                          type="date"
                          InputLabelProps={{
                            shrink: true,
                          }}
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>

                      <Grid item xs={12} sm={6}>
                        <Field
                          as={TextField}
                          label="Máximo de Execuções"
                          name="maxExecutions"
                          type="number"
                          variant="outlined"
                          margin="dense"
                          fullWidth
                        />
                      </Grid>
                    </>
                  )}

                  {/* Configurações de Ticket */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Configurações de Ticket
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>Status do Ticket</InputLabel>
                      <Field
                        as={Select}
                        label="Status do Ticket"
                        name="statusTicket"
                      >
                        <MenuItem value="open">Aberto</MenuItem>
                        <MenuItem value="closed">Fechado</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>Abrir Ticket</InputLabel>
                      <Field
                        as={Select}
                        label="Abrir Ticket"
                        name="openTicket"
                      >
                        <MenuItem value="enabled">Habilitado</MenuItem>
                        <MenuItem value="disabled">Desabilitado</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>Fila</InputLabel>
                      <Field
                        as={Select}
                        label="Fila"
                        name="queueId"
                      >
                        <MenuItem value="">Nenhuma</MenuItem>
                        {(queues || []).map((queue) => (
                          <MenuItem key={queue.id} value={queue.id}>
                            {queue.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl variant="outlined" margin="dense" fullWidth>
                      <InputLabel>Usuário Responsável</InputLabel>
                      <Field
                        as={Select}
                        label="Usuário Responsável"
                        name="userId"
                      >
                        <MenuItem value="">Nenhum</MenuItem>
                        {(users || []).map((user) => (
                          <MenuItem key={user.id} value={user.id}>
                            {user.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("campaignMetaModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting || loading}
                  variant="contained"
                >
                  {campaignMetaId
                    ? i18n.t("campaignMetaModal.buttons.okEdit")
                    : i18n.t("campaignMetaModal.buttons.okAdd")}
                  {(isSubmitting || loading) && (
                    <CircularProgress size={24} style={{ marginLeft: 10 }} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default CampaignMetaModal;
